import Vue from 'vue';
import ActiveFilters from './components/Playbooks/ActiveFilters.vue';
import FilterPill from './components/Subscriptions/FilterPill.vue';
import MultiSelectDropdown from './components/Subscriptions/MultiSelectDropdown.vue';
import Notification from './components/Subscriptions/Notification.vue';
import PlaybookCard from './components/Playbooks/PlaybookCard.vue';
import PlaybookGroup from './components/Playbooks/PlaybookGroup.vue';

import 'es6-promise/auto';

if (document.getElementById('app')) {
    const app = new Vue({
        el: '#app',
        components: {
            'active-filters': ActiveFilters,
            'filter-pill': FilterPill,
            'multi-select-dropdown': MultiSelectDropdown,
            'notification': Notification,
            'playbook-card': PlaybookCard,
            'playbook-group': PlaybookGroup,
        },
        data() {
            return {
                notification: {
                    show: false,
                    type: 'success',
                    title: '',
                    subtitle: '',
                },
                yearOptions: [],
                marketOptions: [],
                topicOptions: [],
                selectedYears: [],
                selectedMarkets: [],
                selectedTopics: [],
            };
        },
        computed: {
            allSelectedFilters() {
                return {
                    years: this.selectedYears,
                    markets: this.selectedMarkets,
                    topics: this.selectedTopics,
                };
            },
            hasAnyFilters() {
                return (
                    this.selectedYears.length > 0 ||
                    this.selectedMarkets.length > 0 ||
                    this.selectedTopics.length > 0
                );
            },
        },
        watch: {
            allSelectedFilters: {
                handler() {
                    this.updateHash();
                    this.updateGroupExpansion();
                },
                deep: true,
            },
        },
        methods: {
            handleYearChange(yearId) {
                if (this.selectedYears.includes(yearId)) {
                    this.selectedYears = this.selectedYears.filter((id) => id !== yearId);
                } else {
                    this.selectedYears.push(yearId);
                }
            },
            handleMarketChange(marketId) {
                if (this.selectedMarkets.includes(marketId)) {
                    this.selectedMarkets = this.selectedMarkets.filter((id) => id !== marketId);
                } else {
                    this.selectedMarkets.push(marketId);
                }
            },
            handleTopicChange(topicId) {
                if (this.selectedTopics.includes(topicId)) {
                    this.selectedTopics = this.selectedTopics.filter((id) => id !== topicId);
                } else {
                    this.selectedTopics.push(topicId);
                }
            },
            handleFilterRemove(filter) {
                if (filter.type === 'year') {
                    this.selectedYears = this.selectedYears.filter((id) => id !== filter.value);
                } else if (filter.type === 'market') {
                    this.selectedMarkets = this.selectedMarkets.filter((id) => id !== filter.value);
                } else if (filter.type === 'topic') {
                    this.selectedTopics = this.selectedTopics.filter((id) => id !== filter.value);
                }
            },
            removeNotification() {
                this.notification.show = false;
            },
            resetAllFilters() {
                this.selectedYears = [];
                this.selectedMarkets = [];
                this.selectedTopics = [];
            },
            updateHash() {
                const parts = [];

                if (this.selectedYears.length > 0) {
                    parts.push(`years=${this.selectedYears.join(',')}`);
                }
                if (this.selectedMarkets.length > 0) {
                    parts.push(`markets=${this.selectedMarkets.join(',')}`);
                }
                if (this.selectedTopics.length > 0) {
                    parts.push(`topics=${this.selectedTopics.join(',')}`);
                }

                if (parts.length > 0) {
                    window.location.hash = parts.join('&');
                } else {
                    window.location.hash = '';
                }
            },
            parseHash() {
                const hash = window.location.hash.replace('#', '');
                if (!hash) return;

                const params = new URLSearchParams(hash);

                const years = params.get('years');
                if (years) {
                    years.split(',').forEach((id) => {
                        const yearId = parseInt(id);
                        if (this.yearOptions.find((y) => y.id === yearId) && !this.selectedYears.includes(yearId)) {
                            this.selectedYears.push(yearId);
                        }
                    });
                }

                const markets = params.get('markets');
                if (markets) {
                    markets.split(',').forEach((id) => {
                        const marketId = parseInt(id);
                        if (this.marketOptions.find((m) => m.id === marketId) && !this.selectedMarkets.includes(marketId)) {
                            this.selectedMarkets.push(marketId);
                        }
                    });
                }

                const topics = params.get('topics');
                if (topics) {
                    topics.split(',').forEach((id) => {
                        const topicId = parseInt(id);
                        if (this.topicOptions.find((t) => t.id === topicId) && !this.selectedTopics.includes(topicId)) {
                            this.selectedTopics.push(topicId);
                        }
                    });
                }
            },
            initializeData() {
                const appElement = document.getElementById('app');
                if (appElement) {
                    try {
                        const rawMarkets = JSON.parse(appElement.dataset.markets || '{}');
                        const rawYears = JSON.parse(appElement.dataset.years || '[]');
                        const rawTopics = JSON.parse(appElement.dataset.topics || '{}');

                        this.marketOptions = Object.entries(rawMarkets).map(([id, name]) => ({
                            id: parseInt(id),
                            value: parseInt(id),
                            name: name,
                            label: name,
                        })).sort((a, b) => a.name.localeCompare(b.name));

                        this.yearOptions = rawYears.map((year) => ({
                            id: year,
                            value: year,
                            name: String(year),
                            label: String(year),
                        }));

                        this.topicOptions = Object.entries(rawTopics).map(([id, name]) => ({
                            id: parseInt(id),
                            value: parseInt(id),
                            name: name,
                            label: name,
                        })).sort((a, b) => a.name.localeCompare(b.name));
                    } catch (e) {
                        console.warn('Failed to parse filter data:', e);
                        this.marketOptions = [];
                        this.yearOptions = [];
                        this.topicOptions = [];
                    }
                }
            },
            updateGroupExpansion() {
                this.$nextTick(() => {
                    let firstVisibleGroupExpanded = false;

                    Object.keys(this.$refs).forEach((refKey) => {
                        if (refKey.startsWith('playbookGroup')) {
                            const groupComponents = Array.isArray(this.$refs[refKey])
                                ? this.$refs[refKey]
                                : [this.$refs[refKey]];

                            groupComponents.forEach((groupComponent) => {
                                if (groupComponent && groupComponent.shouldShowGroup) {
                                    if (firstVisibleGroupExpanded) {
                                        groupComponent.resetExpanded();
                                    } else {
                                        groupComponent.setExpanded(true);
                                        firstVisibleGroupExpanded = true;
                                    }
                                }
                            });
                        }
                    });
                });
            },
            showNotification(type, title, subtitle = '') {
                this.notification = {
                    show: true,
                    type: type,
                    title: title,
                    subtitle: subtitle,
                };

                this.$nextTick(() => {
                    const notificationEl = this.$refs.notification && this.$refs.notification.$el;
                    if (notificationEl) {
                        notificationEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                });
            },
        },
        mounted() {
            this.initializeData();
            this.parseHash();

            // Hide static content and show Vue components
            document.getElementById('app').classList.add('vue-ready');
            this.$nextTick(() => {
                document.querySelectorAll('.vue-content').forEach((el) => {
                    el.style.display = 'block';
                });
            });
        },
    });
}
